import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useCapability } from '../../../../hooks/use-capability';
import { useConversation } from '../../../../hooks/use-conversation';
import { useMessages } from '../../../../hooks/use-messages';
import { usePresence } from '../../../../hooks/use-presence';
import { api } from '../../../../lib/api-client';
import { useAuthStore } from '../../../../stores/auth-store';
import { Composer } from '../../../conversations/components/Composer';
import { relativeTime } from '../../../conversations/format';
import {
  ConversationMirror,
  DevolverAtendenteModal,
  EncaminharOutroModal,
  PageHead,
} from '../../components';
import { useActiveTakeover } from '../../hooks/use-active-takeover';
import { useComposerState, useDiscrepancy } from '../../hooks/use-coattendance';
import { useTakeoverActions } from '../../hooks/use-takeover-actions';
import { adaptMirror } from '../acompanhamento/acompanhamento-adapter';

import type { ConversationTakeoverResult } from '../../types';
import type { PresenceState, SupervisorVariant } from '../../view-models';
import type React from 'react';

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

/** State da navegação vinda da Tela 2 (D1) — carrega o takeover criado no force-takeover. */
interface IntervencaoNavState {
  takeover?: ConversationTakeoverResult;
}

function NoAccess(): React.JSX.Element {
  return (
    <>
      <PageHead title="Intervenção" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <h3 style={{ color: 'var(--fg-1)' }}>Intervenção requer Team-Pro+</h3>
        <p>Assumir uma conversa em nome da supervisão é um recurso de operação em equipe.</p>
      </div>
    </>
  );
}

/** Estado honesto sem takeover ativo (deep-link/refresh sem state — D1; Item 332). */
function NoTakeover({ conversationId }: { conversationId: string | undefined }): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <>
      <PageHead title="Intervenção" />
      <div className="banner banner--info" role="status">
        <span className="banner__icon" aria-hidden="true">
          ℹ
        </span>
        <div className="banner__body">
          <span className="banner__title">Nenhuma intervenção ativa nesta sessão.</span>
          <span className="banner__sub">
            Para assumir uma conversa, abra-a no Acompanhamento e use “Substituir atendente”.
          </span>
        </div>
        <button
          className="btn-ghost-mini banner__action"
          type="button"
          onClick={() => {
            navigate(
              conversationId !== undefined
                ? `/supervisor/acompanhamento/${conversationId}`
                : '/supervisor/acompanhamento',
            );
          }}
        >
          Ir ao Acompanhamento
        </button>
      </div>
    </>
  );
}

function Intervencao({
  conversationId,
  takeover,
  variant,
}: {
  conversationId: string;
  takeover: ConversationTakeoverResult;
  variant: SupervisorVariant;
}): React.JSX.Element {
  const navigate = useNavigate();
  const supervisorName = useAuthStore((s) => s.user?.name) ?? 'Supervisora';
  const canForce = useCapability('force-takeover');

  const conversation = useConversation(conversationId);
  const { messages } = useMessages(conversationId);
  const composer = useComposerState(conversationId);
  const { data: presence } = usePresence();
  const discrepancy = useDiscrepancy(conversationId);

  const actions = useTakeoverActions(takeover.id);

  const [devolverOpen, setDevolverOpen] = useState(false);
  const [encaminharOpen, setEncaminharOpen] = useState(false);

  // Picker real do Encaminhar (D3) — handoff targets do tenant; só busca com o modal aberto.
  const targetsQuery = useQuery({
    queryKey: ['handoff-targets'],
    enabled: encaminharOpen,
    queryFn: async () => (await api.listHandoffTargets()).data,
  });

  const presenceFor = (userId: string): PresenceState => {
    const found = presence.find((p) => p.userId === userId);
    const status = found?.status;
    if (status === 'online' || status === 'away' || status === 'busy' || status === 'offline') {
      return status;
    }
    return 'offline';
  };
  const resolveName = (userId: string): string => {
    const found = (presence as unknown as { userId: string; name?: string }[]).find(
      (p) => p.userId === userId,
    );
    return found?.name ?? userId;
  };

  const leave = (): void => {
    navigate('/supervisor');
  };

  if (conversation.isLoading) {
    return (
      <>
        <PageHead title="Intervenção" />
        <p style={{ padding: 'var(--space-8)', color: 'var(--fg-3)' }}>Carregando conversa…</p>
      </>
    );
  }
  if (conversation.isError || conversation.data === undefined) {
    return (
      <>
        <PageHead title="Intervenção" />
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Não foi possível carregar a conversa.</span>
          </div>
        </div>
      </>
    );
  }

  const vm = adaptMirror({
    conversation: conversation.data,
    messages,
    composer: composer.data,
    presenceFor,
    resolveName,
  });

  const attendantName = vm.header.attendantName;

  // D6 — evento de takeover derivado do dado REAL carregado na navegação. Motivo/tempo
  // só aparecem se houver; nunca "14:32"/motivo de exemplo do protótipo.
  const takeoverReason = takeover.reason ?? null;
  const takeoverAtLabel =
    takeover.executedAt !== undefined
      ? relativeTime(takeover.executedAt).replace(/^há /, '')
      : null;

  // D7 — banner adversarial honesto (mesmo padrão D12 da Tela 2): só com dado real + Regulated.
  const showAdversarialBanner = variant === 'regulated' && discrepancy.indicator !== null;

  // Desfecho honesto da RESPOSTA do servidor (nunca string hardcoded do mockup).
  const returnOutcome =
    actions.returnResult !== null ? 'Conversa devolvida ao atendente original.' : null;
  const transferOutcome =
    actions.transferResult !== null ? 'Conversa encaminhada ao atendente escolhido.' : null;

  const onConfirmDevolver = (returnReason: string): void => {
    void actions.returnToAttendant(returnReason).then((ok) => {
      if (ok !== null) {
        setDevolverOpen(false);
        leave();
      }
    });
  };
  const onConfirmEncaminhar = ({
    targetUserId,
    transferReason,
  }: {
    targetUserId: string;
    transferReason: string;
  }): void => {
    void actions.transfer(targetUserId, transferReason).then((ok) => {
      if (ok !== null) {
        setEncaminharOpen(false);
        leave();
      }
    });
  };

  return (
    <>
      {/* Banner — supervisora assumiu (Devolver/Encaminhar). */}
      <div className="banner banner--warn">
        <span className="banner__icon" aria-hidden="true">
          🔄
        </span>
        <div className="banner__body">
          <span className="banner__title">Atendendo como {supervisorName} (Supervisora)</span>
          <div className="banner__sub">
            {attendantName !== '—'
              ? `esta conversa foi assumida de ${attendantName}`
              : 'esta conversa foi assumida diretamente'}
          </div>
        </div>
        {canForce && (
          <>
            <button
              className="btn-ghost-mini banner__action"
              type="button"
              onClick={() => {
                setEncaminharOpen(true);
              }}
            >
              Encaminhar a outro
            </button>
            <button
              className="btn-ghost-mini btn-ghost-mini--primary banner__action"
              type="button"
              onClick={() => {
                setDevolverOpen(true);
              }}
            >
              Devolver atendente
            </button>
          </>
        )}
      </div>

      {showAdversarialBanner && (
        <div className="banner banner--warn" role="status">
          <div className="banner__body">
            <span className="banner__title">
              Bond adversarial — há divergência de visibilidade entre as partes.
            </span>
            <span style={{ fontSize: 'var(--text-sm)' }}>
              A regra do silêncio se aplica: não revele o que o atendente não vê.
            </span>
          </div>
        </div>
      )}

      <PageHead
        title={`Conversa com ${vm.header.customerName}`}
        subtitle={`Operando como ${supervisorName}`}
      />

      <div style={{ padding: 'var(--space-3) var(--space-5) var(--space-6)' }}>
        <ConversationMirror
          vm={vm}
          monitoringMode="visible-to-both"
          supervisorName={supervisorName}
        />

        {/* Evento centralizado de takeover (D6). */}
        <div className="takeover-event">
          <span className="takeover-event__icon" aria-hidden="true">
            🔄
          </span>
          <div className="takeover-event__body">
            <b>Supervisão assumiu esta conversa ({supervisorName})</b>
            {takeoverReason !== null && (
              <div className="takeover-event__reason mono">
                Motivo registrado: “{takeoverReason}”
              </div>
            )}
            {takeoverAtLabel !== null && (
              <div className="takeover-event__time mono">{takeoverAtLabel}</div>
            )}
          </div>
        </div>

        {/* Nota — atendente original em somente leitura (informativo). */}
        <div className="readonly-note">
          <span aria-hidden="true">📖</span>
          <span>
            {attendantName !== '—' ? <b>{attendantName}</b> : 'O atendente original'} agora vê esta
            conversa em <b>modo somente leitura</b>. Pode visualizar mas não responder.
          </span>
        </div>

        {/* D5 — operar a conversa: após o takeover a conversa é do supervisor (H_a). Reusa o
            composer de envio do Atendimento (mesmo endpoint POST /conversations/:id/messages). */}
        <Composer conversation={conversation.data} />
      </div>

      <DevolverAtendenteModal
        open={devolverOpen}
        attendantName={attendantName !== '—' ? attendantName : null}
        isPending={actions.isReturning}
        failed={actions.returnError !== null}
        outcome={returnOutcome}
        onClose={() => {
          setDevolverOpen(false);
        }}
        onConfirm={onConfirmDevolver}
      />
      <EncaminharOutroModal
        open={encaminharOpen}
        targets={targetsQuery.data ?? []}
        isLoadingTargets={targetsQuery.isLoading}
        targetsError={targetsQuery.isError}
        isPending={actions.isTransferring}
        failed={actions.transferError !== null}
        outcome={transferOutcome}
        onClose={() => {
          setEncaminharOpen(false);
        }}
        onConfirm={onConfirmEncaminhar}
      />
    </>
  );
}

export function IntervencaoPage(): React.JSX.Element {
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canForce = useCapability('force-takeover');
  const { conversationId } = useParams<{ conversationId: string }>();
  const location = useLocation();
  const navState = (location.state ?? null) as IntervencaoNavState | null;
  const navTakeover = navState?.takeover ?? null;

  // Item 332 — fallback de deep-link/refresh: sem o nav state da Tela 2, redescobre
  // o takeover ativo no banco. Query GATEADA: só dispara com a cap, sem nav state
  // e com conversationId (gatear o fetch, não só o render — evita 403 e fetch ocioso).
  const activeTakeover = useActiveTakeover(conversationId, {
    enabled: canForce && navTakeover === null,
  });

  if (variant === 'solo' || !canForce) return <NoAccess />;
  if (conversationId === undefined) return <NoTakeover conversationId={conversationId} />;

  // Não piscar negativo falso ("Nenhuma intervenção") durante o fetch inicial do
  // fallback. `isLoading` (só na 1ª carga sem dado) evita re-piscar em refetch de foco.
  if (navTakeover === null && activeTakeover.isLoading) {
    return (
      <>
        <PageHead title="Intervenção" />
        <p style={{ padding: 'var(--space-8)', color: 'var(--fg-3)' }}>Carregando intervenção…</p>
      </>
    );
  }

  // Falha do fallback é distinta de "não há takeover": não degradar para o
  // NoTakeover (negativo falso) — banner honesto de erro.
  if (navTakeover === null && activeTakeover.isError) {
    return (
      <>
        <PageHead title="Intervenção" />
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">
              Não foi possível verificar se há uma intervenção ativa nesta conversa.
            </span>
            <span className="banner__sub">Tente recarregar a página.</span>
          </div>
        </div>
      </>
    );
  }

  const takeover = navTakeover ?? activeTakeover.data ?? null;
  if (takeover === null) return <NoTakeover conversationId={conversationId} />;
  return <Intervencao conversationId={conversationId} takeover={takeover} variant={variant} />;
}
